<script lang="ts">
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { formatIST } from '$lib/utils/dates';
  // Shared read-only detail view for an auto-logged `email` touchpoint —
  // used from both ActivitiesPage and CompanyDetail (see the Task 1 build
  // brief: "everywhere activities render"). Editing an email activity
  // through the normal form doesn't make sense (it's a synced record), so
  // this is the only way to see/act on one beyond the list row.
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Download, Mail } from 'lucide-svelte';
  import { activitiesApi, emailMessagesApi } from '$lib/api/crm';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { Activity } from '$lib/types';

  const {
    activity,
    onClose,
    onDismissed
  }: { activity: Activity; onClose: () => void; onDismissed: () => void } = $props();

  const queryClient = useQueryClient();
  const email = $derived(activity.email);

  // Full parsed body — the review UI's own read of the stored raw email
  // (see GET /api/email-messages/:id/body). retry: false so a 404 ("no raw
  // stored") falls back to the snippet immediately instead of retrying.
  const bodyQuery = createQuery(() => ({
    queryKey: ['email-message-body', email?.id],
    queryFn: () => emailMessagesApi.body((email as NonNullable<typeof email>).id),
    enabled: !!email,
    retry: false
  }));

  const dismissMutation = createMutation(() => ({
    mutationFn: (id: number) => activitiesApi.remove(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['company-detail'] });
      if (activity.id === id) { onDismissed(); onClose(); }
    }
  }));

  function dismiss() {
    const id = activity.id;
    scheduleUndo(`Dismiss email #${id} as not relevant`, () => dismissMutation.mutate(id));
  }

  let downloading = $state(false);
  async function download() {
    if (!email) return;
    downloading = true;
    try {
      await emailMessagesApi.download(email.id, `${email.subject || 'email'}.eml`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      downloading = false;
    }
  }

  function fmt(iso: string | null): string {
    return formatIST(iso);
  }
</script>

<Modal title={email?.subject || activity.title || 'Email'} {onClose} maxWidth="900px">
  {#if email}
    <div class="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[.06em] text-dark-gray">
      <Mail class="h-3.5 w-3.5" /> Synced from mailbox
    </div>
    <div class="space-y-2 text-[13px]">
      <div class="flex gap-2"><span class="w-14 shrink-0 text-dark-gray">From</span><span class="font-medium text-navy">{email.from_addr || '—'}</span></div>
      <div class="flex gap-2"><span class="w-14 shrink-0 text-dark-gray">To</span><span class="font-medium text-navy">{email.to_addrs || '—'}</span></div>
      {#if email.cc_addrs}
        <div class="flex gap-2"><span class="w-14 shrink-0 text-dark-gray">Cc</span><span class="font-medium text-navy">{email.cc_addrs}</span></div>
      {/if}
      <div class="flex gap-2"><span class="w-14 shrink-0 text-dark-gray">Sent</span><span class="font-medium text-navy">{fmt(email.sent_at)}</span></div>
    </div>

    {#if bodyQuery.isLoading}
      <p class="tym-loading-state mt-4">Loading message&hellip;</p>
    {:else if bodyQuery.isSuccess && bodyQuery.data.html}
      {#if bodyQuery.data.truncated}
        <p class="mt-4 text-[12px] font-medium text-amber-700">Message truncated — showing a partial copy.</p>
      {/if}
      <iframe
        title="Email body"
        sandbox=""
        srcdoc={bodyQuery.data.html}
        class="mt-2 h-[65vh] w-full rounded-lg border border-[#EFEEED] bg-white"
      ></iframe>
    {:else if bodyQuery.isSuccess && bodyQuery.data.text}
      {#if bodyQuery.data.truncated}
        <p class="mt-4 text-[12px] font-medium text-amber-700">Message truncated — showing a partial copy.</p>
      {/if}
      <p class="mt-2 max-h-[65vh] overflow-y-auto whitespace-pre-wrap rounded-lg border border-[#EFEEED] bg-[#FBFBFA] p-3 text-[13px] text-[#666]">
        {bodyQuery.data.text}
      </p>
    {:else if email.snippet}
      <p class="mt-4 whitespace-pre-line rounded-lg border border-[#EFEEED] bg-[#FBFBFA] p-3 text-[13px] text-[#666]">
        {email.snippet}
      </p>
    {/if}

    <div class="mt-5 flex flex-wrap justify-end gap-2">
      <button type="button" class="btn-danger" onclick={dismiss} disabled={dismissMutation.isPending}>
        {dismissMutation.isPending ? 'Dismissing...' : 'Dismiss — not relevant'}
      </button>
      <button
        type="button"
        class="btn-secondary"
        onclick={download}
        disabled={!email.has_raw || downloading}
        title={email.has_raw ? 'Download original .eml' : 'No stored copy of this email'}
      >
        <Download class="h-4 w-4" />
        {downloading ? 'Downloading...' : 'Download original (.eml)'}
      </button>
    </div>
  {:else}
    <p class="text-[13px] text-dark-gray">No email metadata available for this touchpoint.</p>
  {/if}
</Modal>
